import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { modulesStoreOf } from '../display-context.util';
import { ReadonlyDisplayBase } from './readonly-display.base';
import { ValueDisplayComponent } from './value-display.component';

/**
 * Read display for a `module-select` answer (#21239): the module's TITLE instead of its id.
 *
 * `module-select.component.ts:83-86` stores the picked module's ID and builds its OWN options
 * straight from `ModulesStore.enabledModules()` — never on `props.options` — so
 * `displayContextFor`'s `lookupOptionLabel` has nothing to look the id up against and the read
 * row printed the raw GUID before this display existed.
 *
 * Resolution mirrors that editable field EXACTLY — same store slice
 * (`ModulesStore.enabledModules()`), same `id`/`title` pair — so a module reads identically
 * whichever mode the page is in. The store comes from `modulesStoreOf` (`display-context.util.ts`)
 * — the same accessor `displayContextFor` already calls for the taxonomy resolver, reading
 * `field.options.formState.modulesStore` (`DetailsService` publishes it on every entry form) with
 * ONE shared guard: a `modulesStore` of the wrong shape reads as "no store" there, not as a throw
 * inside this computed. NOT an injected `ModulesStore`: a display that injects its own store would
 * render differently in Storybook/standalone forms, where no store exists, than on a real detail
 * page (Global Constraint 1).
 *
 * An id the store does not know (module disabled/deleted since the answer was saved) and a
 * missing store (Storybook, a standalone form) both fall back to the id itself, not an empty
 * row — a stored answer is never nothing, even when it cannot be resolved to a name.
 *
 * Presentational: both inputs come from the row, the module list from the field's formState.
 */
@Component({
  selector: 'dd-module-select-display',
  imports: [ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span, or the row's
  // truncate/multiline behaviour would stop at this element.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (title(); as title) {
      {{ title }}
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class ModuleSelectDisplayComponent extends ReadonlyDisplayBase {
  /**
   * The module's title, or a falsy value when it cannot be resolved: `null` for an empty/non-string
   * id, an id the store does not carry, or no store at all — but `''` when the store DOES carry
   * the module and its own title happens to be empty (`find(...)?.title ?? null` only replaces
   * `null`/`undefined`, never `''`). Either falsy value falls back to `<dd-value-display>` in the
   * template's `@if`, which prints the stored id as-is (the same em-dash-for-empty rule every
   * other field gets) — the two cases render identically, only the exact returned value differs.
   */
  readonly title = computed(() => {
    const id = this.value();
    if (typeof id !== 'string' || !id) {
      return null;
    }
    const store = modulesStoreOf(this.field());
    return store?.enabledModules().find(module => module.id === id)?.title ?? null;
  });
}
